/**
 * "New forecast" CTA for A9 Predictive Growth. Generates 3 scenarios with
 * confidence bands.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { LineChart, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import type { Forecast, Project } from '@seo/types';
import { Button, type ButtonProps } from '@/components/ui/button';
import { FormDialog, FormDialogBlock } from '@/components/ui/form-dialog';
import { TextField, FormField } from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractFieldErrors, summarizeApiError } from '@/lib/api';
import { useCreateForecast } from '@/features/forecasts/use-create-forecast';
import { useCampaigns } from '@/features/campaigns/use-campaigns';

const ForecastFormSchema = z.object({
  project_id: z.string().uuid({ message: 'Pick a project' }),
  horizon_days: z.number().int().min(7).max(365).default(90),
  metric: z
    .enum(['organic_clicks', 'impressions', 'rankings', 'conversions'])
    .default('organic_clicks'),
  campaign_id: z.string().uuid().optional().or(z.literal('')),
  calendar_id: z.string().uuid().optional().or(z.literal('')),
  synthetic_daily_value: z.number().nonnegative().default(250),
  synthetic_trend_per_day: z.number().default(0.5),
});
type ForecastFormValues = z.infer<typeof ForecastFormSchema>;

export interface ForecastCreateButtonProps {
  projects: Project[];
  defaultProjectId?: string;
  label?: string;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  navigateOnSuccess?: boolean;
}

export function ForecastCreateButton({
  projects,
  defaultProjectId,
  label = 'New forecast',
  buttonProps,
  navigateOnSuccess = true,
}: ForecastCreateButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const trigger = (
    <Button {...buttonProps}>
      <TrendingUp className="h-4 w-4" />
      {label}
    </Button>
  );

  if (projects.length === 0) {
    return (
      <FormDialogBlock
        open={open}
        onOpenChange={setOpen}
        trigger={trigger}
        title="No projects yet"
        description="Create a project first."
      />
    );
  }

  return (
    <ForecastDialog
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      projects={projects}
      defaultProjectId={defaultProjectId}
      navigateOnSuccess={navigateOnSuccess}
    />
  );
}

function ForecastDialog({
  open,
  onOpenChange,
  trigger,
  projects,
  defaultProjectId,
  navigateOnSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trigger: JSX.Element;
  projects: Project[];
  defaultProjectId?: string;
  navigateOnSuccess: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateForecast();
  const initialProjectId =
    defaultProjectId ?? (projects.length === 1 ? projects[0]?.id : '') ?? '';

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ForecastFormValues>({
    resolver: zodResolver(ForecastFormSchema),
    defaultValues: {
      project_id: initialProjectId,
      horizon_days: 90,
      metric: 'organic_clicks',
      synthetic_daily_value: 250,
      synthetic_trend_per_day: 0.5,
    },
    mode: 'onBlur',
  });

  const projectId = watch('project_id');
  const metric = watch('metric');
  const campaignId = watch('campaign_id');
  const campaigns = useCampaigns(projectId || undefined);
  // Only offer plans that have (or will have) something to score. Bare drafts
  // and cancelled/failed plans contribute nothing to the sensitivity table.
  const eligibleCampaigns = (campaigns.data?.items ?? []).filter((c) =>
    ['executing', 'evaluating', 'completed'].includes(c.status),
  );

  useEffect(() => {
    // If the project changes, drop a stale campaign binding.
    if (campaignId && !eligibleCampaigns.some((c) => c.id === campaignId)) {
      setValue('campaign_id', '', { shouldValidate: false });
    }
  }, [campaignId, eligibleCampaigns, setValue]);

  useEffect(() => {
    if (!projectId && projects.length === 1) {
      setValue('project_id', projects[0]!.id, { shouldValidate: false });
    }
  }, [projectId, projects, setValue]);

  const submit = handleSubmit(async (values) => {
    try {
      const created = await create.mutateAsync({
        project_id: values.project_id,
        horizon_days: values.horizon_days,
        metric: values.metric,
        ...(values.campaign_id ? { campaign_id: values.campaign_id } : {}),
        ...(values.calendar_id ? { calendar_id: values.calendar_id } : {}),
        synthetic_baseline: {
          daily_value: values.synthetic_daily_value,
          trend_per_day: values.synthetic_trend_per_day,
        },
      });
      toast.success('Forecast queued', {
        description: `${values.horizon_days}-day ${values.metric.replace('_', ' ')}`,
      });
      reset();
      onOpenChange(false);
      if (navigateOnSuccess) {
        await navigate({
          to: '/forecasts/$forecastId',
          params: { forecastId: (created as Forecast).id },
        });
      }
    } catch (e) {
      const fieldErrors = extractFieldErrors(e);
      for (const f of ['project_id', 'horizon_days', 'metric'] as const) {
        if (fieldErrors[f]) setError(f, { type: 'server', message: fieldErrors[f] });
      }
      const summary = summarizeApiError(e, {
        fallbackTitle: 'Could not create forecast',
        fieldLabels: {
          project_id: 'Project',
          horizon_days: 'Horizon',
          metric: 'Metric',
        },
      });
      toast.error(summary.title, { description: summary.description });
    }
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      icon={<LineChart className="h-5 w-5 text-primary" />}
      title="Generate a forecast"
      description="We'll fit a baseline to history (or a synthetic seed) and produce three scenarios with confidence bands."
      size="lg"
      submitLabel="Generate"
      submitting={isSubmitting || create.isPending}
      onSubmit={submit}
    >
      <FormField label="Project" error={errors.project_id?.message}>
        <Select
          value={projectId}
          onValueChange={(v) => setValue('project_id', v, { shouldValidate: true })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Pick a project" />
          </SelectTrigger>
          <SelectContent>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <div className="grid grid-cols-2 gap-3">
        <FormField label="Metric">
          <Select
            value={metric}
            onValueChange={(v) =>
              setValue('metric', v as ForecastFormValues['metric'], {
                shouldValidate: true,
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="organic_clicks">Organic clicks</SelectItem>
              <SelectItem value="impressions">Impressions</SelectItem>
              <SelectItem value="rankings">Rankings</SelectItem>
              <SelectItem value="conversions">Conversions</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
        <TextField
          label="Horizon (days)"
          type="number"
          min={7}
          max={365}
          {...register('horizon_days', { valueAsNumber: true })}
        />
      </div>

      <FormField
        label="Bind to campaign (optional)"
        hint="Ties the sensitivity numbers to a specific plan. Leave empty for a project-wide, baseline-only forecast."
      >
        <Select
          value={campaignId || 'none'}
          onValueChange={(v) =>
            setValue('campaign_id', v === 'none' ? '' : v, { shouldValidate: false })
          }
          disabled={!projectId}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                projectId
                  ? eligibleCampaigns.length === 0
                    ? 'No eligible plans on this project yet'
                    : "Baseline only — don't bind"
                  : 'Pick a project first'
              }
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Baseline only — don&apos;t bind</SelectItem>
            {eligibleCampaigns.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.goal.length > 60 ? `${c.goal.slice(0, 57)}…` : c.goal} · {c.status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <div className="space-y-1.5">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Synthetic baseline (when no history exists yet)
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="Daily value"
            type="number"
            step="1"
            min={0}
            {...register('synthetic_daily_value', { valueAsNumber: true })}
          />
          <TextField
            label="Trend per day"
            type="number"
            step="0.1"
            {...register('synthetic_trend_per_day', { valueAsNumber: true })}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Only used when no real GA4/GSC history is available for the project.
        </p>
      </div>
    </FormDialog>
  );
}
